<script>
import {computed, ref} from 'vue';
import {useMenuStore} from '@/js/store/MenuStore.js';
import {useSwipePanel} from '@/js/useSwipePanel.js';
import IconX from '@js/components/Icons/IconX.vue';
import {Link} from '@inertiajs/vue3';
import MobileMenuDropdownBlock from '@js/components/Mobile/Components/MobileMenuDropdownBlock.vue';
import SalesDepartmentPhone from '@js/components/Sections/Links/SalesDepartmentPhone.vue';
import IconLogo from '@js/components/Icons/IconLogo.vue';
import {useLikeStore} from "@js/store/LikeStore.js";
import IconHeart from "@js/components/Icons/IconHeart.vue";
import IconScales from "@js/components/Icons/IconScales.vue";
import {useCompareStore} from "@js/store/CompareStore.js";

export default {
    name: 'MobileMenu',
    components: {
        IconScales,
        IconHeart,
        IconLogo,
        SalesDepartmentPhone,
        MobileMenuDropdownBlock,
        IconX,
        Link,
    },

    setup() {
        const menuStore = useMenuStore();
        const likeStore = useLikeStore();
        const compareStore = useCompareStore();

        const panelRef = ref(null);
        const handleRef = ref(null);

        const isOpen = computed(() => menuStore.isSidenavOpen);

        const {
            rendered,
            panelStyle,
            backdropStyle,
            close,
            handleBackgroundClick,
        } = useSwipePanel({
            isOpen,
            panelRef,
            handleRef,
            direction: 'left',
            closeThreshold: 90,
            openDuration: 300,
            closeDuration: 280,
            fadeDivisor: 220,
            lockScroll: true,
            onClose: () => {
                menuStore.closeSidenav();
            },
        });

        const hasLink = (item) => {
            return !!(item?.url || item?.route_name);
        };

        const buildMenuLink = (item) => {
            if (item?.url) {
                return item.url;
            }

            if (!item?.route_name) {
                return null;
            }

            const fullParams = item.route_params ?? {};
            const {query, ...pureRouteParams} = fullParams;

            let url = route(item.route_name, pureRouteParams);

            if (query && typeof query === 'object') {
                const queryParams = new URLSearchParams();

                for (const [key, value] of Object.entries(query)) {
                    if (value === null || value === undefined) continue;

                    if (Array.isArray(value) || typeof value === 'object') {
                        queryParams.set(key, JSON.stringify(value));
                    } else {
                        queryParams.set(key, String(value));
                    }
                }

                const queryString = queryParams.toString();

                if (queryString) {
                    url += url.includes('?') ? `&${queryString}` : `?${queryString}`;
                }
            }

            return url;
        };

        const closeMenu = () => {
            close();
        };

        const onMenuLinkClick = () => {
            close();
        };

        return {
            menuStore,
            likeStore,
            compareStore,
            panelRef,
            handleRef,
            rendered,
            panelStyle,
            backdropStyle,
            closeMenu,
            handleBackgroundClick,
            hasLink,
            buildMenuLink,
            onMenuLinkClick,
        };
    },
};
</script>

<template>
    <div v-if="rendered">
        <div
            class="fixed inset-0 z-40 bg-black/50"
            :style="backdropStyle"
            @click="handleBackgroundClick"
        ></div>

        <div
            ref="panelRef"
            class="fixed inset-y-0 left-0 z-50 w-full max-w-[480px] bg-white shadow-xl"
            :style="panelStyle"
        >
            <div
                ref="handleRef"
                class="absolute top-0 right-0 z-20 flex h-full w-3 items-center justify-center cursor-grab"
            >
                <div class="h-14 w-1 rounded-full bg-gray-300"></div>
            </div>

            <div class="flex h-full flex-col min-h-0">
                <div class="shrink-0 flex items-center justify-between px-4 py-3 shadow">
                    <IconLogo class="h-auto w-[200px]"/>

                    <button
                        @click.prevent="closeMenu"
                        title="Закрыть"
                        aria-label="Закрыть"
                        class="rounded-md focus:outline-none"
                    >
                        <IconX class="h-6 w-6"/>
                    </button>
                </div>

                <nav class="max-h-full flex-grow overflow-y-auto px-4 pt-5">
                    <div class="mb-2 grid grid-cols-2 divide-x divide-gray-500">
                        <div class="flex items-center justify-center">
                            <Link
                                :href="route('likedProducts')"
                                class="flex gap-2 font-medium"
                                @click="onMenuLinkClick"
                            >
                                <IconHeart class="h-5 w-5 text-navHoverColor"/>
                                Избранные ({{ likeStore.likedIds.length ?? 0 }})
                            </Link>
                        </div>

                        <div class="flex items-center justify-center">
                            <Link
                                :href="route('compareProducts')"
                                class="flex gap-2 font-medium"
                                @click="onMenuLinkClick"
                            >
                                <IconScales class="h-5 w-5 text-blue-500"/>
                                Сравнение ({{ compareStore.comparedIds.length ?? 0 }})
                            </Link>
                        </div>
                    </div>

                    <ul>
                        <template v-for="(menuItem, itemKey) in menuStore.menuItems" :key="itemKey">
                            <li class="flex items-start gap-3 border-b py-2.5">
                                <div
                                    v-if="menuItem.sub_menu && menuItem.sub_menu.length"
                                    class="w-full"
                                >
                                    <MobileMenuDropdownBlock
                                        :route_name="menuItem.route_name"
                                        :route_params="menuItem.route_params"
                                        :btnText="menuItem.title"
                                        :items="menuItem.sub_menu"
                                        :icon_path="menuItem.icon ?? null"
                                    />
                                </div>

                                <div v-else class="w-full">
                                    <component
                                        :is="hasLink(menuItem) ? 'Link' : 'div'"
                                        :href="hasLink(menuItem) ? buildMenuLink(menuItem) : undefined"
                                        class="group flex items-center rounded-lg py-2 text-base font-medium text-gray-900"
                                        @click="hasLink(menuItem) ? onMenuLinkClick() : null"
                                    >
                                        <span>{{ menuItem.title }}</span>
                                    </component>
                                </div>
                            </li>
                        </template>
                    </ul>
                </nav>

                <!--
                <div class="mt-auto">
                    <div class="flex flex-col items-center justify-center">
                        <div class="w-full ps-4">
                            <SalesDepartmentPhone />
                        </div>
                    </div>
                </div>
                -->
            </div>
        </div>
    </div>
</template>
